import { Button, Checkbox, Modal, TextInput } from '@mantine/core'
import { IconArrowRight } from '@tabler/icons-react'
import { useId, useState } from 'react'
import type { PatientMergeInput } from '@/hooks/usePatientMerge'
import { AccountStatus } from './RecordColumn'
import type { RecordSummary } from './reviewFormat'

const CONFIRM_TEXT = 'Merge'

export type MergeRequest = Omit<PatientMergeInput, 'confirmedSamePerson' | 'confirmText'>

type Props = {
  opened: boolean
  onClose: () => void
  /** Starts the merge; the page owns the mutation, so its result lands even if this modal closes. */
  onConfirm: (input: PatientMergeInput) => void
  /** The merge request is in flight: the modal cannot be dismissed. */
  pending: boolean
  kept: RecordSummary
  merged: RecordSummary
  /** The merged record's associated records, one "Label: count" each — they stay with it. */
  mergedRecordCounts: string[]
  request: MergeRequest
}

function RecordCard({
  caption,
  note,
  summary,
}: {
  caption: string
  note: string
  summary: RecordSummary
}) {
  return (
    <div
      role="group"
      aria-label={caption}
      className="flex min-w-0 flex-1 flex-col gap-1 rounded-[12px] border border-[#E7E9EC] p-3"
    >
      <span className="text-[11px] font-semibold tracking-wider text-[#8A94A6] uppercase">
        {caption}
      </span>
      <span className="text-[11px] text-[#8A94A6]">{note}</span>
      <span className="truncate font-semibold text-[#1F2733]">{summary.name}</span>
      <span className="truncate text-[13px] text-[#6B7280]">{summary.email ?? 'No email'}</span>
      <AccountStatus summary={summary} />
    </div>
  )
}

/**
 * The last step before a merge: which record is kept and which is retired, what happens to the
 * retired record's own records, and two deliberate acts — the same-patient checkbox and typing
 * exactly "Merge". The server re-checks all of it; Cancel changes nothing. Not dismissible while
 * the request runs.
 */
export function MergeConfirmModal({ opened, onClose, pending, ...rest }: Props) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Confirm merge"
      size="lg"
      centered
      closeOnEscape={!pending}
      closeOnClickOutside={!pending}
      withCloseButton={!pending}
    >
      {/* Mounted only while open, so the checkbox and typed text start empty every time. */}
      <ConfirmBody onClose={onClose} pending={pending} {...rest} />
    </Modal>
  )
}

function ConfirmBody({
  onClose,
  onConfirm,
  pending,
  kept,
  merged,
  mergedRecordCounts,
  request,
}: Omit<Props, 'opened'>) {
  const [confirmed, setConfirmed] = useState(false)
  const [typed, setTyped] = useState('')
  const countsHeadingId = useId()
  const ready = confirmed && typed === CONFIRM_TEXT

  const submit = () => {
    if (ready) onConfirm({ ...request, confirmedSamePerson: true, confirmText: CONFIRM_TEXT })
  }

  return (
    <div className="flex flex-col gap-4 text-[13px] text-[#4A5160]">
      <div className="flex items-stretch gap-2">
        <RecordCard caption="Merge this record into it" note="Retired" summary={merged} />
        <div className="flex items-center text-[#8A94A6]">
          <IconArrowRight size={20} aria-hidden />
          <span className="sr-only">merges into</span>
        </div>
        <RecordCard caption="Keep this record" note="Stays as it is" summary={kept} />
      </div>

      <p className="m-0 rounded-md bg-[#F6F7F9] p-3 text-[#1F2733]">
        {merged.name}&apos;s records are not moved to {kept.name}. They stay with the retired record
        and are deleted with it when the retention period ends; until then a super admin can restore
        it.
      </p>

      {mergedRecordCounts.length > 0 && (
        <div>
          <p className="mb-1.5 font-semibold text-[#1F2733]" id={countsHeadingId}>
            Staying with {merged.name}
          </p>
          <ul aria-labelledby={countsHeadingId} className="m-0 flex list-none flex-col gap-1 p-0">
            {mergedRecordCounts.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}

      {request.reason && (
        <p className="m-0">
          <span className="font-semibold text-[#1F2733]">Reason: </span>
          {request.reason}
        </p>
      )}

      <Checkbox
        checked={confirmed}
        onChange={(event) => setConfirmed(event.currentTarget.checked)}
        label="I have verified that both records belong to the same patient."
      />
      <TextInput
        label={`Type "${CONFIRM_TEXT}" to confirm`}
        value={typed}
        onChange={(event) => setTyped(event.currentTarget.value)}
        autoComplete="off"
      />

      <div className="flex justify-end gap-2">
        <Button variant="default" onClick={onClose} disabled={pending}>
          Cancel
        </Button>
        <Button color="red" disabled={!ready} loading={pending} onClick={submit}>
          Merge records
        </Button>
      </div>
    </div>
  )
}
